import { createApi } from '@reduxjs/toolkit/query/react';

import {
  CreateIncidentSchema,
  IncidentMentionSchema,
  IncidentSchema,
  UpdateIncidentSchema,
} from '../types/incident-types';
import { baseQueryWithErrorHandling } from './baseQuery';

// Incident API
export const incidentApiClient = createApi({
  reducerPath: 'incidentApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['Incidents', 'Incident'] as const,
  endpoints: (builder) => ({
    getIncidents: builder.query<IncidentSchema[], { matterId: string }>({
      query: ({ matterId }) => {
        const url = `api/matters/${matterId}/incidents/`;
        return url;
      },
      providesTags: (result, error, { matterId }) => [
        { type: 'Incidents', id: matterId },
      ],
    }),
    getIncident: builder.query<
      IncidentSchema,
      { matterId: string; incidentId: string }
    >({
      query: ({ matterId, incidentId }) => ({
        url: `api/matters/${matterId}/incidents/${incidentId}/`,
        method: 'GET',
      }),
      providesTags: (result, error, { matterId, incidentId }) => [
        { type: 'Incident', id: `${matterId}-${incidentId}` },
      ],
    }),
    createIncident: builder.mutation<
      IncidentSchema,
      { matterId: string; incident: Partial<CreateIncidentSchema> }
    >({
      query: ({ matterId, incident }) => ({
        url: `api/matters/${matterId}/incidents/`,
        method: 'POST',
        body: { ...incident, matter_id: matterId },
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'Incidents', id: matterId },
      ],
    }),
    updateIncident: builder.mutation<
      IncidentSchema,
      {
        matterId: string;
        incidentId: string;
        incident: Partial<UpdateIncidentSchema>;
      }
    >({
      query: ({ matterId, incidentId, incident }) => ({
        url: `api/matters/${matterId}/incidents/${incidentId}/`,
        method: 'PATCH',
        body: { ...incident, id: incidentId },
      }),
      invalidatesTags: (result, error, { matterId, incidentId }) => [
        { type: 'Incidents', id: matterId },
        { type: 'Incident', id: `${matterId}-${incidentId}` },
      ],
    }),
    deleteIncident: builder.mutation<
      void,
      { matterId: string; incidentId: string }
    >({
      query: ({ matterId, incidentId }) => ({
        url: `api/matters/${matterId}/incidents/${incidentId}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'Incidents', id: matterId },
      ],
    }),
    regenerateIncident: builder.mutation<
      void,
      { matterId: string; artifactIds: string[] }
    >({
      query: ({ matterId, artifactIds }) => ({
        url: `api/matters/${matterId}/mentioned-incidents/regenerate_mentions/`,
        method: 'POST',
        body: { artifact_ids: artifactIds },
      }),
      invalidatesTags: (result, error, { matterId }) => [
        { type: 'Incidents', id: matterId },
      ],
    }),
  }),
});

// Mentioned Incidents API
export const mentionedIncidentsApiClient = createApi({
  reducerPath: 'mentionedIncidentsApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['MentionedIncidents', 'MentionedIncident'] as const,
  endpoints: (builder) => ({
    getMentionedIncidents: builder.query<
      IncidentMentionSchema[],
      {
        matterId: string;
        artifactId?: string;
        incidentId?: string;
        mentionIds?: string[];
      }
    >({
      query: ({ matterId, artifactId, incidentId, mentionIds }) => {
        const url = `api/matters/${matterId}/mentioned-incidents/`;
        const params = new URLSearchParams();

        if (artifactId) params.append('artifact_id', artifactId);
        if (incidentId) params.append('incident_id', incidentId);
        if (mentionIds?.length)
          params.append('mention_ids', mentionIds.join(','));

        const queryString = params.toString();
        return queryString ? `${url}?${queryString}` : url;
      },
      providesTags: (result, error, { matterId }) => [
        { type: 'MentionedIncidents', id: matterId },
      ],
    }),
  }),
});

export const {
  useGetIncidentsQuery,
  useGetIncidentQuery,
  useCreateIncidentMutation,
  useUpdateIncidentMutation,
  useDeleteIncidentMutation,
  useRegenerateIncidentMutation,
} = incidentApiClient;

export const { useGetMentionedIncidentsQuery } = mentionedIncidentsApiClient;
